import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, Download, ChevronDown, Sparkles, LogOut, Cloud, User, Flame, Settings, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import AccountSettings from './AccountSettings';

const Navbar = () => {
  const { lang, setLang, t, theme, toggleTheme } = useLang();
  const { user, login, logout, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    if (window.location.pathname !== '/') { window.location.href = '/#' + id; return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const menuItems = [
    { label: t.nav.products, action: () => scrollTo('features') },
    { label: t.nav.create, action: () => scrollTo('templates') },
    { label: t.nav.learn, action: () => scrollTo('showcase') },
    { label: lang === 'sv' ? 'Trendar' : 'رائج', href: '/trends', icon: Flame },
    { label: t.nav.pricing, action: () => scrollTo('pricing') },
    { label: t.nav.business, action: () => scrollTo('cta') }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/85 dark:bg-neutral-950/85 backdrop-blur-xl border-b border-neutral-200/60 dark:border-white/10' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
        <div className="flex items-center gap-10">
          <a href="#" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center relative overflow-hidden">
              <Sparkles className="w-5 h-5 text-white relative z-10" strokeWidth={2.5} />
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/0 to-cyan-400/40" />
            </div>
            <span className="font-black text-[19px] tracking-tight text-neutral-900 dark:text-white">CapCute</span>
          </a>
          <ul className="hidden lg:flex items-center gap-1">
            {menuItems.map((m, i) => {
              const Icon = m.icon;
              const commonCls = "px-4 py-2 text-[14.5px] font-medium text-neutral-700 dark:text-neutral-300 hover:text-black dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 rounded-full transition-colors flex items-center gap-1 cursor-pointer";
              return (
                <li key={i}>
                  {m.href ? (
                    <a href={m.href} className={commonCls}>
                      {Icon && <Icon className="w-3.5 h-3.5 text-rose-500" fill="currentColor" />}
                      {m.label}
                    </a>
                  ) : (
                    <button onClick={m.action} className={commonCls}>
                      {m.label}
                      <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            title={theme === 'dark' ? (lang === 'sv' ? 'Ljust läge' : 'الوضع الفاتح') : (lang === 'sv' ? 'Mörkt läge' : 'الوضع الداكن')}
            className="w-9 h-9 flex items-center justify-center text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-white/10 rounded-full transition-colors"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <div className="relative">
            <button
              onClick={() => setLangOpen(v => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-[14px] font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-white/10 rounded-full transition-colors"
            >
              <Globe className="w-4 h-4" />
              {lang === 'sv' ? 'SV' : 'AR'}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {langOpen && (
              <div className="absolute top-full mt-2 right-0 rtl:right-auto rtl:left-0 bg-white border border-neutral-200 rounded-xl shadow-lg overflow-hidden w-32 py-1">
                <button onMouseDown={() => setLang('sv')} className={`w-full text-left rtl:text-right px-3 py-2 text-sm hover:bg-neutral-100 ${lang==='sv'?'font-semibold':''}`}>Svenska</button>
                <button onMouseDown={() => setLang('ar')} className={`w-full text-left rtl:text-right px-3 py-2 text-sm hover:bg-neutral-100 ${lang==='ar'?'font-semibold':''}`}>العربية</button>
              </div>
            )}
          </div>
          <button className="hidden md:block px-4 py-2 text-[14.5px] font-medium text-neutral-800 dark:text-neutral-100 hover:text-black dark:hover:text-white rounded-full transition-colors" onClick={() => !user && login()}>
            {user ? '' : t.nav.login}
          </button>
          {user ? (
            <>
              <NotificationBell />
              <div className="relative">
              <button
                onClick={() => setUserMenuOpen(v => !v)}
                onBlur={() => setTimeout(() => setUserMenuOpen(false), 180)}
                className="flex items-center gap-2 h-10 pl-1 pr-3 rtl:pl-3 rtl:pr-1 rounded-full hover:bg-neutral-100 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center text-white text-sm font-bold">{user.name?.[0] || 'U'}</div>
                )}
                <span className="hidden md:inline text-[13.5px] font-semibold text-neutral-900 max-w-[100px] truncate">{user.name?.split(' ')[0]}</span>
                <ChevronDown className="w-3.5 h-3.5 text-neutral-500" />
              </button>
              {userMenuOpen && (
                <div className="absolute top-full mt-2 right-0 rtl:right-auto rtl:left-0 bg-white border border-neutral-200 rounded-2xl shadow-xl overflow-hidden w-64 py-2 z-50">
                  <div className="px-4 py-3 border-b border-neutral-100">
                    <div className="text-[13.5px] font-bold text-neutral-900 truncate">{user.name}</div>
                    <div className="text-[11.5px] text-neutral-500 truncate">{user.email}</div>
                  </div>
                  <a href="/mina-videor" className="w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-neutral-800 hover:bg-neutral-50">
                    <Cloud className="w-4 h-4 text-cyan-500" />
                    <span>{lang === 'sv' ? 'Mina videor' : 'فيديوهاتي'}</span>
                  </a>
                  <button onMouseDown={() => setAccountOpen(true)} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-neutral-800 hover:bg-neutral-50">
                    <Settings className="w-4 h-4 text-neutral-600" />
                    <span>{lang === 'sv' ? 'Konto & data' : 'الحساب والبيانات'}</span>
                  </button>
                  <div className="border-t border-neutral-100 mt-1 pt-1">
                    <button onMouseDown={logout} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-red-600 hover:bg-red-50">
                      <LogOut className="w-4 h-4" />
                      <span>{lang === 'sv' ? 'Logga ut' : 'تسجيل الخروج'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
            </>
          ) : (
            <Button onClick={login} className="h-10 px-5 rounded-full bg-black hover:bg-neutral-800 text-white font-semibold text-[14px] shadow-sm">
              <Download className="w-4 h-4 mr-1.5 rtl:mr-0 rtl:ml-1.5" />
              {t.nav.download}
            </Button>
          )}
          <button className="lg:hidden p-2 -mr-2" onClick={() => setOpen(v => !v)} aria-label="menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-200 px-5 py-4">
          <ul className="flex flex-col gap-1">
            {menuItems.map((m, i) => (
              <li key={i}><a href={m.href} className="w-full block text-left rtl:text-right px-3 py-2.5 text-[15px] font-medium text-neutral-800 rounded-lg hover:bg-neutral-100">{m.label}</a></li>
            ))}
            <li className="pt-2 border-t border-neutral-200 mt-2">
              <button className="w-full text-left rtl:text-right px-3 py-2.5 text-[15px] font-medium text-neutral-800">{t.nav.login}</button>
            </li>
          </ul>
        </div>
      )}
      <AccountSettings open={accountOpen} onClose={() => setAccountOpen(false)} />
    </header>
  );
};

export default Navbar;
